package com.arenaproplay.admin.ui.layouts.shell

import com.arenaproplay.admin.navigation.Routes

/** A single nav destination shown in the 72 px icon rail. */
data class NavItem(
    val route: String,
    val label: String,
    /** Lucide icon name used by [LucideIcon]. */
    val icon: String,
    /** Section title shown in the topbar when this item is active. */
    val group: String,
    /**
     * TODO R6-R8: temporary legacy target used by the sidebar until the
     * consolidated page lands at [route].  When null the sidebar navigates
     * to [route] directly.  Set to the old primary route so the sidebar
     * never dead-ends while the new pages are placeholders.
     */
    val legacySidebarTarget: String? = null,
)

/** A group of nav items with an eyebrow title (used by CommandPalette). */
data class NavGroup(val title: String, val items: List<NavItem>)

// ── Night Arena IA — 9 surfaces ──────────────────────────────────────────────
//
// Sidebar order (top→bottom):
//   Центр / Аналитика / Юзеры / Клубы / События / Модерация / Входящие /
//   Content Studio / Система
//   bottom: Настройки (gear, pinned)
//
// legacySidebarTarget is the OLD primary route that already has content.
// The sidebar navigates there until R6-R8 land the consolidated page at the
// new route.  The new route constants exist in Routes.kt for forward compat.

val appNavGroups: List<NavGroup> = listOf(
    NavGroup(
        title = "Главное",
        items = listOf(
            // «Центр» merged into /analytics — dashboard data now lives on the
            // «Источники и гео» tab; /dashboard redirects there.
            NavItem(
                route  = Routes.ANALYTICS,
                label  = "Аналитика",
                icon   = "bar-chart-2",
                group  = "Аналитика",
                // R5: /analytics shell landed — sidebar navigates to the real page.
                legacySidebarTarget = null,
            ),
        )
    ),
    NavGroup(
        title = "Операции",
        items = listOf(
            NavItem(
                route = Routes.USERS,
                label = "Юзеры",
                icon  = "users",
                group = "Юзеры",
            ),
            NavItem(
                route = Routes.PEOPLE,
                label = "Пипл",
                icon  = "contact",
                group = "Пипл",
            ),
            NavItem(
                route = Routes.CLUBS,
                label = "Клубы",
                icon  = "building-2",
                group = "Клубы",
            ),
            NavItem(
                route = Routes.EVENTS,
                label = "События",
                icon  = "calendar",
                group = "События",
            ),
            NavItem(
                route               = Routes.MODERATION,
                label               = "Модерация",
                icon                = "shield-check",
                group               = "Модерация",
                // R7: consolidated page landed at /moderation — legacy /social redirects here.
                legacySidebarTarget = null,
            ),
            NavItem(
                route               = Routes.INBOX,
                label               = "Входящие",
                icon                = "inbox",
                group               = "Входящие",
                // R7: consolidated page landed at /inbox — legacy /support redirects here.
                legacySidebarTarget = null,
            ),
            NavItem(
                route = Routes.CONTENT_STUDIO,
                label = "Content Studio",
                icon  = "sparkles",
                group = "Content Studio",
            ),
            NavItem(
                route = Routes.APP_LAUNCH,
                label = "App Launch",
                icon  = "megaphone",
                group = "App Launch",
            ),
            NavItem(
                route = Routes.NEWS,
                label = "Новости",
                icon  = "newspaper",
                group = "Новости",
            ),
        )
    ),
    NavGroup(
        title = "Система",
        items = listOf(
            NavItem(
                route               = Routes.SYSTEM,
                label               = "Система",
                icon                = "layers",
                group               = "Система",
                // R8: consolidated page landed at /system — sidebar navigates directly.
                legacySidebarTarget = null,
            ),
        )
    ),
)

/** Settings item pinned to bottom of rail (rendered separately from main list). */
val settingsNavItem = NavItem(
    route = Routes.SETTINGS,
    label = "Настройки",
    icon  = "settings",
    group = "Настройки",
)

/** Flat map from route prefix → NavItem for topbar/breadcrumb resolution. */
val routeNavMap: Map<String, NavItem> by lazy {
    buildMap {
        for (group in appNavGroups) {
            for (item in group.items) {
                put(item.route, item)
                // Also register legacySidebarTarget so breadcrumb resolves for
                // users who navigate to the legacy route via CommandPalette.
                item.legacySidebarTarget?.let { legacy ->
                    if (!containsKey(legacy)) put(legacy, item)
                }
            }
        }
        put(settingsNavItem.route, settingsNavItem)
    }
}

/**
 * Returns (group, pageLabel) for the given [path], or null if not found.
 * Matches exact route first, then prefix (e.g. /clubs/123 → Клубы).
 */
fun breadcrumbFor(path: String): Pair<String, String>? {
    routeNavMap[path]?.let { return it.group to it.label }
    // prefix match for detail pages (/clubs/xxx, /users/xxx, /events/xxx)
    val match = routeNavMap.entries
        .filter { (route, _) -> route != Routes.HOME && path.startsWith("$route/") }
        .maxByOrNull { it.key.length }
        ?.value
    return match?.let { it.group to it.label }
}

// ── CommandPalette flat list ──────────────────────────────────────────────────
// R6 done: Behavior, Insights, Subscriptions → /analytics tabs (redirects).
// R7 done: Social → /moderation; Support, Chat → /inbox tabs (redirects).
// R8 done: Admins, AuditLog, ApiStats → /system tabs (redirects); group removed.

val commandPaletteGroups: List<NavGroup> = listOf(
    NavGroup(
        title = "Night Arena",
        items = appNavGroups.flatMap { it.items } + listOf(settingsNavItem)
    ),
)
